import { create } from 'zustand';
import { Task } from '../types';
import { AITaskManager } from '../lib/ai-agent';
import { supabase } from '../lib/supabase';
import { persist } from 'zustand/middleware';

interface TaskState {
  tasks: Task[];
  addTask: (title: string, description: string) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<void>;
  toggleStep: (taskId: string, stepId: string) => Promise<void>;
  completeTask: (taskId: string) => Promise<void>;
  analyzeAndUpdateTasks: () => Promise<void>;
  fetchTasks: () => Promise<void>;
}

export const useTaskStore = create<TaskState>()(
  persist(
    (set, get) => ({
      tasks: [],

      fetchTasks: async () => {
        const { data: session } = await supabase.auth.getSession();
        if (!session.session?.user.id) return;

        const { data: tasks, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('user_id', session.session.user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching tasks:', error);
          return;
        }

        set({ tasks: tasks as Task[] });
      },

      addTask: async (title: string, description: string) => {
        const { data: session } = await supabase.auth.getSession();
        if (!session.session?.user.id) return;

        const analysis = await AITaskManager.analyzeNewTask(title, description);
        
        const newTask: Task = {
          id: crypto.randomUUID(),
          title,
          description,
          priority: analysis.priority,
          dueDate: analysis.suggestedDueDate,
          status: 'active',
          progress: 0,
          steps: analysis.suggestedSteps.map(step => ({
            id: crypto.randomUUID(),
            description: step.description,
            completed: false
          })),
          aiReasoning: analysis.reasoning,
          user_id: session.session.user.id,
          created_at: new Date().toISOString()
        };

        const { error } = await supabase.from('tasks').insert(newTask);
        
        if (error) {
          console.error('Error adding task:', error);
          return;
        }

        set(state => ({
          tasks: [newTask, ...state.tasks]
        }));
      },

      updateTask: async (taskId: string, updates: Partial<Task>) => {
        const { error } = await supabase
          .from('tasks')
          .update(updates)
          .eq('id', taskId);

        if (error) {
          console.error('Error updating task:', error);
          return;
        }

        set(state => ({
          tasks: state.tasks.map(task =>
            task.id === taskId ? { ...task, ...updates } : task
          )
        }));
      },

      toggleStep: async (taskId: string, stepId: string) => {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;

        const updatedSteps = task.steps.map(step =>
          step.id === stepId ? { ...step, completed: !step.completed } : step
        );

        const progress = Math.round(
          (updatedSteps.filter(step => step.completed).length / updatedSteps.length) * 100
        );

        await get().updateTask(taskId, { steps: updatedSteps, progress });
      },

      completeTask: async (taskId: string) => {
        const updates = {
          status: 'completed',
          progress: 100,
          completedAt: new Date().toISOString(),
          steps: get().tasks.find(t => t.id === taskId)?.steps.map(step => ({ ...step, completed: true }))
        };

        await get().updateTask(taskId, updates);
      },

      analyzeAndUpdateTasks: async () => {
        const currentTasks = get().tasks;
        const analyzedTasks = await AITaskManager.analyzeTasks(currentTasks);
        
        for (const task of analyzedTasks) {
          if (JSON.stringify(task) !== JSON.stringify(currentTasks.find(t => t.id === task.id))) {
            await get().updateTask(task.id, task);
          }
        }
      }
    }),
    {
      name: 'neuro-tasks',
      partialize: (state) => ({ tasks: state.tasks }),
    }
  )
);